// Velkoobchodní cena proti běžné (2026-09-13, mock schválený Tomášem týž den).
//
// Přihlášený zákazník s velkoobchodním ceníkem (VO1–VO3) platí cenu, kterou mu
// Shoptet vyrenderuje. `priceRatio` je u něj 1 (ceník není procentní sleva),
// takže z dataLayeru se výhoda vyčíst nedá. Běžnou cenu nese index vyhledávání:
// `/specs` položka `prices: {mo, retail}` (search/api/card.mjs `pricesOf`),
// `mo` = standardní cena hlavního ceníku bez akce, `retail` = aktuální cena
// nepřihlášeného (akční, když běží).
//
// Pravidla (výpočet VO cen vlastní reporting-saas F34 / ADR 0036, shrnutí
// `docs/ops/vo-ceniky.md`):
//   • VO cenu šablona NEPOČÍTÁ — bere ji z vyrenderované karty, detailu nebo
//     dataLayeru košíku;
//   • procento i úspora jen ze dvou čísel (floor jako Shoptet,
//     `productPrice.discountPercent`), nikdy z konstanty 10/15/20 %;
//   • VO výhoda (`state: 'vo'`) jen když floor((běžná − VO) / běžná) > 0
//     A ZÁROVEŇ VO < cena nepřihlášeného;
//   • výprodej (`state: 'sale_blocked'`, mock schválený 2026-09-14): běží akce
//     (cena nepřihlášeného < běžná) a VO cena je jí ROVNÁ — po F34 se sleva
//     ceníku počítá z akční ceny s podlahou marže, takže rovnost znamená, že
//     podlaha slevu spolkla. Šablona pak ukáže akci (škrtnutá běžná, žlutý
//     badge) a větu, že se sleva ceníku neuplatní. VO cena NAD akcí je jiný
//     stav (automat reporting-saas ještě nedojel) → nic, jinak by stránka
//     tvrdila „výprodejová cena" u ceny vyšší než akce. Cenu nepřihlášeného
//     VO stránka nenese (VO ceník akci nemá, `.price-standard` nevznikne),
//     proto jen z indexu;
//   • stránka sama nese slevu (nativní `.price-standard`) → nekreslí se nic,
//     přeškrtnutí Shoptetu má přednost (dvě škrtnuté ceny by si protiřečily);
//   • variantní produkty se přeskakují — kód karty je master, index ho nezná
//     a cena se mění s variantou.
//
// Index jede za platformou: změna jen v ceníku (`PATCH /pricelists`) do něj
// dorazí nočním full reindexem. Nikdy nehází — bez konfigurace, bez API nebo
// bez `prices` (starší generace indexu) se nekreslí nic.

import { specsFor } from './specsCache.js';
import { getWholesaleConfig } from './searchConfig.js';
import { discountPercent, formatPrice, parseNumber } from './productPrice.js';
import { NBSP } from './money.js';
import { VO } from '../locale/cs/common.js';
import SHOP from '../../config/shop.js';

// Úrovně z `/b2b/` podle `priceListId` Shoptetu = `wholesale.pricelists`
// z manifestu shopu (název a sleva úrovně z pravidla ceníku, F34). Procento je
// POPIS ÚROVNĚ pro Moje slevy — cena produktu ani úspora z něj nikdy nevznikají.
// Neznámý ceník = obecné „Velkoobchodní cena" bez názvu i procenta.
export const PRICELIST_NAMES = SHOP.wholesale.pricelists;
export const HAS_WHOLESALE = Object.keys(PRICELIST_NAMES).length > 0;

const LABEL_PRICE = VO.price;
const LABEL_FROM_RETAIL = VO.fromRetail;
const LABEL_SAVE = VO.save;
const LABEL_CARD_TAG = VO.tag;
const LABEL_CARD_SALE = VO.saleCardTag;

/** Název úrovně pro `priceListId`, nebo `null`. */
export function pricelistName(id) {
  const level = PRICELIST_NAMES[Number(id)];
  return level ? level.name : null;
}

/** Sleva úrovně v procentech pro `priceListId`, nebo `null`. */
export function pricelistDiscount(id) {
  const level = PRICELIST_NAMES[Number(id)];
  return level ? level.discountPct : null;
}

/**
 * Přihlášený zákazník s jiným než hlavním ceníkem → `{priceListId, name,
 * discountPct}`, jinak `null`. `customer` = blok `shoptet.customer` z dataLayeru.
 */
export function wholesaleCustomer(customer) {
  // shop bez VO ceníků v manifestu (SK, `wholesale.pricelists: {}`) VO vrstvu
  // nemá — ani kdyby dataLayer vrátil jiný než hlavní ceník (S4 #400)
  if (!HAS_WHOLESALE) return null;
  if (!customer || customer.registered !== true) return null;
  const id = Number(customer.priceListId);
  if (!Number.isFinite(id) || id === 1) return null;
  return { priceListId: id, name: pricelistName(id), discountPct: pricelistDiscount(id) };
}

/**
 * Stav VO ceny proti běžné, nebo `null` = nekreslit.
 * `prices` = `/specs` `{mo, retail}`, `vo` = cena, kterou zákazník na stránce vidí.
 * → `{state: 'vo' | 'sale_blocked', mo, percent, saved}`; procento a úspora
 * vždy z běžné ceny a zobrazené ceny (u výprodeje je to sleva akce).
 */
export function wholesaleAdvantage(prices, vo) {
  if (!prices) return null;
  // Celé haléře: VO ceny mají dvě desetinná místa a float by z 699 → 559,20
  // udělal 19,999… → „−19 %" místo −20 %, z 99 → 98,01 by výhodu skryl úplně
  // (Codex review #366). Procento i úspora jdou z haléřových celých čísel.
  const mo = halere(prices.mo);
  const retail = halere(prices.retail);
  const price = halere(vo);
  if (mo === null || retail === null || price === null || price <= 0) return null;
  // VO pod cenou nepřihlášeného = výhoda ceníku. Rovná při běžící akci =
  // podlaha marže slevu spolkla (výprodej). Vyšší = data (automat nedojel) → nic.
  let state = null;
  if (price < retail) state = 'vo';
  else if (price === retail && retail < mo) state = 'sale_blocked';
  if (!state) return null;
  const percent = discountPercent(mo, price); // floor jako nativní „–12 %"
  if (percent === null) return null;
  return { state: state, mo: mo / 100, percent: percent, saved: (mo - price) / 100 };
}

/** Částka v celých haléřích, nebo `null` pro nečíslo. */
function halere(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/** „Velkoobchodní cena · Jipos Premium" (bez známého ceníku jen první část). */
export function priceLabel(ctx) {
  return ctx && ctx.name ? LABEL_PRICE + ' · ' + ctx.name : LABEL_PRICE;
}

/** „−20 % z běžné ceny · ušetříte 578 Kč" pro badge detailu. */
export function saveText(adv, currency) {
  return '−' + adv.percent + NBSP + '% ' + LABEL_FROM_RETAIL + ' · ' + LABEL_SAVE + ' ' + formatPrice(adv.saved, currency);
}

/** „Na výprodejovou cenu se sleva ceníku Jipos Premium neuplatní." */
export function saleNoteText(ctx) {
  return ctx && ctx.name ? VO.saleNote + ctx.name + VO.saleNoteEnd : VO.saleNoteGeneric;
}

/** aria-label ikony účtu: „Můj účet, ceník Jipos Premium" (bez názvu obecně). */
export function accountLabel(ctx, base) {
  const head = base || VO.account;
  return ctx && ctx.name ? head + VO.accountPricelist + ctx.name : head + ', ' + VO.pricelistGeneric.toLowerCase();
}

// ---------------------------------------------------------------------------
// Ceny z indexu: kód → Promise<{mo, retail}|null>, jeden GET na kód za stránku
// ---------------------------------------------------------------------------

/** Dávka kódů na jeden `/specs` GET (strop API je 60). */
const BATCH = 48;
const PENDING = new Map();
const KNOWN = new Map();

/**
 * Běžné ceny pro kódy: `Map<code, {mo, retail}|null>`. `null` = produkt
 * v indexu není nebo cenu nemá (nebo API mlčí) — obojí znamená „nekreslit".
 */
export async function pricesFor(codes) {
  const unique = [];
  const missing = [];
  (codes || []).forEach(function (code) {
    if (!code || unique.indexOf(code) >= 0) return;
    unique.push(code);
    if (!PENDING.has(code)) missing.push(code);
  });

  for (let i = 0; i < missing.length; i += BATCH) {
    const chunk = missing.slice(i, i + BATCH);
    // sdílená cache `/specs` (specsCache.js): chipy, Bestseller i ceny = jeden GET
    const req = specsFor(chunk, getWholesaleConfig()).then(function (items) {
      const byCode = new Map();
      items.forEach(function (item, code) {
        if (item && item.prices) byCode.set(code, item.prices);
      });
      return byCode;
    });
    chunk.forEach(function (code) {
      PENDING.set(
        code,
        req.then(function (byCode) {
          const prices = byCode.get(code) || null;
          KNOWN.set(code, prices);
          return prices;
        }),
      );
    });
  }

  const out = new Map();
  await Promise.all(
    unique.map(function (code) {
      return PENDING.get(code).then(function (prices) {
        out.set(code, prices);
      });
    }),
  );
  return out;
}

/**
 * Synchronní čtení pro místa, která se překreslují sama (souhrn pokladny):
 * `undefined` = ještě nevíme, `null` = nemá, jinak `{mo, retail}`.
 */
export function knownPrices(code) {
  return KNOWN.has(code) ? KNOWN.get(code) : undefined;
}

/**
 * Dotáhne ceny kódů, na které se ještě nikdo neptal, a po doběhnutí zavolá
 * `onReady`. Když nic nového není, `onReady` se nevolá (překreslení by jinak
 * volalo samo sebe donekonečna).
 */
export function ensurePrices(codes, onReady) {
  if (!getWholesaleConfig()) return;
  const fresh = (codes || []).filter(function (code) {
    return code && !PENDING.has(code);
  });
  if (!fresh.length) return;
  pricesFor(fresh).then(function () {
    if (typeof onReady === 'function') onReady();
  });
}

// ---------------------------------------------------------------------------
// Karty produktu (výpis, značka, hledání, carousely)
// ---------------------------------------------------------------------------

const CARD_SELECTOR = '.p[data-micro="product"]';

function codeOf(card) {
  const el = card.querySelector('[data-micro="sku"]');
  if (el) return (el.textContent || '').trim();
  // karta z indexu (productCardRender.js) nese kód v atributu
  return card.dataset.bsCode || '';
}

/**
 * Karta s cenou jednoho kódu: nativní s formulářem do košíku, nebo karta z indexu
 * (hledání, `/popular`, „Podobné skladem") bez variant. Variantní nativní karta
 * formulář nemá (jen odkaz na výběr varianty), variantní karta z indexu nese
 * `data-bs-variants`.
 */
function priceableCard(card) {
  if (card.querySelector('form.pr-action')) return true;
  return !!card.dataset.bsCode && !card.dataset.bsVariants;
}

/**
 * Karty z indexu nesou VO cenu z API (`pl` = ceník zákazníka), takže
 * se čtou stejně jako nativní — z `.price-final`.
 *
 * Přeškrtnutá běžná cena v cenové pilulce + štítek „VO −N %" nad ní (tmavý),
 * u výprodeje „Výprodej −N %" (žlutý). Idempotentní; bez stavu nebo nad
 * nativní slevou nic.
 */
export function mountCardWholesale(card, prices) {
  if (!card || card.dataset.bsVoDone) return;
  const pill = card.querySelector('.bs-card__pill');
  const final = pill && pill.querySelector('.price-final');
  if (!final || pill.querySelector('.price-standard, .price-save')) return;
  const adv = wholesaleAdvantage(prices, parseNumber(final.textContent));
  if (!adv) return;
  card.dataset.bsVoDone = '1';

  const was = document.createElement('s');
  was.className = 'bs-card__vo-was';
  was.textContent = formatPrice(adv.mo);
  pill.appendChild(was);

  const sale = adv.state === 'sale_blocked';
  const tag = document.createElement('span');
  tag.className = 'bs-card__vo' + (sale ? ' bs-card__vo--sale' : '');
  tag.textContent = (sale ? LABEL_CARD_SALE : LABEL_CARD_TAG) + ' −' + adv.percent + NBSP + '%';
  pill.appendChild(tag);
}

function pendingCards(root) {
  const out = [];
  const cards = (root || document).querySelectorAll(CARD_SELECTOR);
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    // productCard.js kartu ještě nedekoroval → pilulka neexistuje, přijde znovu
    if (card.dataset.bsVo || !card.dataset.bsCard) continue;
    card.dataset.bsVo = '1';
    if (card.classList.contains('bs-card--mini')) continue;
    if (!priceableCard(card)) continue;
    out.push(card);
  }
  return out;
}

async function runCards(root) {
  const cards = pendingCards(root);
  if (!cards.length) return;
  const byCode = new Map();
  cards.forEach(function (card) {
    const code = codeOf(card);
    if (!code) return;
    if (!byCode.has(code)) byCode.set(code, []);
    byCode.get(code).push(card);
  });
  if (!byCode.size) return;
  const prices = await pricesFor([...byCode.keys()]);
  prices.forEach(function (value, code) {
    if (!value) return;
    (byCode.get(code) || []).forEach(function (card) {
      mountCardWholesale(card, value);
    });
  });
}

let scheduled = false;
function schedule() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(function () {
    scheduled = false;
    runCards(document);
  });
}

/**
 * VO cena na kartách. Idempotentní (re-init po ShoptetDOMPageContentLoaded);
 * karty dosypané AJAXem (loadMore, filtr, carousely) chytá observer. Mimo
 * přihlášený velkoobchod se nespustí vůbec.
 */
export function initWholesaleCards() {
  if (!getWholesaleConfig()) return;
  runCards(document);

  if (document.body.dataset.bsVoObserved) return;
  document.body.dataset.bsVoObserved = '1';
  new MutationObserver(function (muts) {
    for (let i = 0; i < muts.length; i++) {
      const added = muts[i].addedNodes;
      for (let j = 0; j < added.length; j++) {
        const node = added[j];
        if (node.nodeType === 1 && (node.matches(CARD_SELECTOR) || node.querySelector(CARD_SELECTOR))) {
          schedule();
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}
